import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';

// Create axios instance with base URL
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001'
});

// Debug logging
console.log('Environment check on load:', {
  STRIPE_KEY: process.env.REACT_APP_STRIPE_PUBLIC_KEY,
  NODE_ENV: process.env.NODE_ENV,
  ALL_ENV: process.env
});

// Initialize Stripe
const initializeStripe = () => {
  const key = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  
  if (!key) {
    console.error('Stripe key missing. Available env vars:', Object.keys(process.env));
    return null;
  }
  
  console.log('Initializing Stripe with key:', key.substring(0, 10) + '...');
  return loadStripe(key);
};

const stripePromise = initializeStripe();

const PaymentForm = ({ tier, onSuccess, onError, fetchCredits }) => {
  const { user } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);

    try {
      const token = localStorage.getItem('token');
      
      // 1. Create payment intent
      const { data: intent } = await axiosInstance.post(
        '/api/payments/create-intent',
        { tierId: tier.id.toLowerCase() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // 2. Confirm card payment
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        intent.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              email: user?.email
            }
          }
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // 3. Add credits after successful payment
      await axiosInstance.post('/api/credits/add', 
        {
          amount: tier.credits,
          paymentIntentId: intent.paymentIntentId
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // 4. Show success state
      setCompleted(true);
      onSuccess('Payment successful! Credits have been added to your account.');
      
      // 5. Refresh credits and page after delay
      await fetchCredits();
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Payment error:', error);
      onError(error.message);
    } finally {
      setProcessing(false);
    }
  };

  if (completed) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Payment Successful!
        </Typography>
        <Typography color="text.secondary">
          {tier.credits} credits have been added to your account.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Refreshing page...
        </Typography>
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <CardElement 
        options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#9e2146',
            },
          },
        }}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={!stripe || processing}
        sx={{ mt: 2 }}
      >
        {processing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            Processing...
          </Box>
        ) : (
          `Pay $${tier.price}`
        )}
      </Button>
    </form>
  );
};

const CreditsPage = () => {
  const { token, user } = useAuth();
  const [credits, setCredits] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [showPayment, setShowPayment] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [stripeError, setStripeError] = useState(null);

  useEffect(() => {
    // Log environment variables in component
    console.log('Component mounted. Environment check:', {
      STRIPE_KEY: process.env.REACT_APP_STRIPE_PUBLIC_KEY,
      NODE_ENV: process.env.NODE_ENV
    });

    if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
      setStripeError('Stripe configuration missing - check environment variables');
    }
  }, []);

  // Move fetchCredits inside component
  const fetchCredits = async () => {
    try {
      const response = await axiosInstance.get('/api/credits/balance', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCredits(response.data.balance);
    } catch (error) {
      console.error('Error fetching credits:', error);
      showMessage('Error fetching credit balance', 'error');
    }
  };

  // Update useEffect to use the fetchCredits function
  useEffect(() => {
    if (token) {
      fetchCredits();
    }
  }, [token]); // Add fetchCredits to dependency array if needed

  const showMessage = (text, severity = 'info') => {
    setMessage(text);
    setSeverity(severity);
  };

  const pricingTiers = [
    {
      id: 'bronze',
      name: 'Bronze Tier',
      price: 97,
      credits: 10,
      perCreditPrice: 9.70,
      color: '#cd7f32',
      featured: false,
      features: [
        '10 Quiz Credits',
        'Additional credits at $9 each',
        'Basic Support',
        'Valid for 6 months'
      ]
    },
    {
      id: 'silver',
      name: 'Silver Tier',
      price: 297,
      credits: 100,
      perCreditPrice: 2.97,
      color: '#4caf50',
      featured: true,
      features: [
        '100 Quiz Credits',
        'Additional credits at $3 each',
        'Priority Support',
        'Valid for 12 months',
        'Bulk discount'
      ]
    },
    {
      id: 'gold',
      name: 'Gold Tier',
      price: 997,
      credits: 500,
      perCreditPrice: 1.99,
      color: '#ffd700',
      featured: false,
      features: [
        '500 Quiz Credits',
        'Additional credits at $2 each',
        'Premium Support',
        'Valid for 12 months',
        'Maximum discount'
      ]
    }
  ];

  const handlePurchase = (tier) => {
    console.log('Selected tier for purchase:', {
      id: tier.id,
      name: tier.name,
      price: tier.price,
      credits: tier.credits
    });
    setSelectedTier(tier);
    setShowPayment(true);
  };

  const handlePaymentSuccess = async () => {
    await fetchCredits(); // Now fetchCredits is in scope
    setShowPayment(false);
    setSelectedTier(null);
    showMessage('Payment successful! Credits have been added to your account.', 'success');
  };

  const handlePaymentError = (error) => {
    showMessage(error, 'error');
  };

  const handleDevCredits = async () => {
    try {
      setIsLoading(true);
      const devCredits = 100;
      
      const response = await axiosInstance.post(
        'http://localhost:5001/api/credits/add',
        { amount: devCredits },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        setCredits(response.data.balance);
        showMessage('Development credits added successfully!', 'success');
      }
    } catch (error) {
      showMessage('Error adding development credits', 'error');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestCredits = async () => {
    try {
      setIsLoading(true);
      if (!token || !user) {
        throw new Error('Not authenticated');
      }

      await axiosInstance.post(
        'http://localhost:5001/api/credits/request',
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      showMessage('Credit request sent to team leader', 'success');
    } catch (err) {
      console.error('Error requesting credits:', err);
      showMessage('Failed to send credit request', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Verify Stripe initialization
    if (!stripePromise) {
      setStripeError('Payment system is not available - configuration missing');
    } else {
      stripePromise.catch(error => {
        console.error('Stripe initialization error:', error);
        setStripeError(error.message);
      });
    }
  }, []);

  // Add role check for school admin
  const canManageCredits = user?.role === 'corporate' || 
                          user?.role === 'admin' || 
                          user?.role === 'school_admin';
  
  // Check if user is a team member (not leader)
  const isTeamMember = user?.team_id && 
                       user?.role !== 'corporate' && 
                       user?.role !== 'school_admin' && 
                       user?.role !== 'admin';
  
  // If user is a team member, show restricted access message
  if (isTeamMember) {
    return (
      <Container>
        <Alert severity="info" sx={{ mt: 4 }}>
          Credit purchases are managed by your team leader.
        </Alert>
      </Container>
    );
  }
  
  // Regular users (individual or corporate) can purchase credits
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 6 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            color: 'white',
            mb: 4
          }}
        >
          <Typography variant="h3" gutterBottom>
            Your Credit Balance: {credits}
          </Typography>
          <Typography variant="subtitle1">
            Each assessment requires 1 credit
          </Typography>
        </Paper>

        {message && (
          <Alert 
            severity={severity}
            sx={{ mt: 2, mb: 4 }}
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        )}

        <Grid container spacing={4} alignItems="stretch">
          {pricingTiers.map((tier) => (
            <Grid item xs={12} md={4} key={tier.id}>
              <Card 
                elevation={tier.featured ? 8 : 3}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)'
                  },
                  ...(tier.featured && {
                    border: `2px solid ${tier.color}`,
                  })
                }}
              >
                {tier.featured && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      bgcolor: tier.color,
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    Best Value
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Typography variant="h4" gutterBottom sx={{ color: tier.color }}>
                    {tier.name}
                  </Typography>
                  <Typography variant="h3" gutterBottom>
                    ${tier.price}
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom>
                      {tier.credits} Credits
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      ${tier.perCreditPrice.toFixed(2)} per credit
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 3 }} />
                  <List>
                    {tier.features.map((feature, index) => (
                      <ListItem key={index} sx={{ py: 1 }}>
                        <ListItemIcon>
                          <CheckCircleIcon sx={{ color: tier.color }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature}
                          primaryTypographyProps={{
                            sx: { fontWeight: index === 0 ? 'bold' : 'normal' }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions sx={{ p: 4, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={tier.featured ? "contained" : "outlined"}
                    size="large"
                    onClick={() => handlePurchase(tier)}
                    disabled={isLoading}
                    sx={{
                      py: 1.5,
                      ...(tier.featured && {
                        bgcolor: tier.color,
                        '&:hover': {
                          bgcolor: tier.color,
                          filter: 'brightness(0.9)'
                        }
                      })
                    }}
                  >
                    {isLoading && selectedTier === tier.id ? 'Processing...' : 'Select Plan'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {user?.accountType === 'corporate' && credits === 0 && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" gutterBottom>
              Need more credits?
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleRequestCredits}
              disabled={isLoading}
              startIcon={<PersonIcon />}
              sx={{ mt: 1 }}
            >
              {isLoading ? 'Sending Request...' : 'Request Credits from Team Leader'}
            </Button>
          </Box>
        )}

        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Divider sx={{ my: 4 }} />
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDevCredits}
              disabled={isLoading}
            >
              {isLoading ? 'Adding Credits...' : 'Add Development Credits'}
            </Button>
          </Box>
        )}

        {canManageCredits && (
          <Box>
            <Typography variant="h6">
              Add Credits {user.role === 'school_admin' ? 'for Team Members' : ''}
            </Typography>
            {/* Credit management form */}
          </Box>
        )}

        {/* Payment Dialog */}
        <Dialog 
          open={showPayment} 
          onClose={() => {
            setShowPayment(false);
            setSelectedTier(null);
            setStripeError(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Purchase Credits
            <IconButton
              aria-label="close"
              onClick={() => {
                setShowPayment(false);
                setSelectedTier(null);
                setStripeError(null);
              }}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {stripeError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {stripeError}
              </Alert>
            ) : !stripePromise ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Payment system is not available
              </Alert>
            ) : (
              <>
                {selectedTier && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {selectedTier.name}
                    </Typography>
                    <Typography color="text.secondary">
                      {selectedTier.credits} credits for ${selectedTier.price}
                    </Typography>
                  </Box>
                )}
                {selectedTier && (
                  <Elements stripe={stripePromise}>
                    <PaymentForm
                      tier={selectedTier}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      fetchCredits={fetchCredits}
                    />
                  </Elements>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CreditsPage;
